import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Switch, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getAuditConfig,
  createAuditConfig,
  updateAuditConfig,
  type CreateAuditConfigRequest,
  type UpdateAuditConfigRequest,
} from '@/api/auditApi';
import { useAuth } from '@/hooks/useAuth';

const ACTIONS = ['create', 'update', 'delete'];

interface ConfigFormValues {
  resource: string;
  actions: string[];
  is_enabled: boolean;
  retention_days: number;
  log_details: boolean;
  notify_on_action: boolean;
}

export default function AuditConfigForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<ConfigFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getAuditConfig(id)
      .then((config) => {
        form.setFieldsValue({
          resource: config.resource,
          actions: config.actions,
          is_enabled: config.is_enabled,
          retention_days: config.retention_days,
          log_details: config.log_details,
          notify_on_action: config.notify_on_action,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: ConfigFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateAuditConfigRequest = {
          actions: values.actions,
          is_enabled: values.is_enabled,
          retention_days: values.retention_days,
          log_details: values.log_details,
          notify_on_action: values.notify_on_action,
        };
        await updateAuditConfig(id, dto);
        message.success(t('audit.config_update_success'));
        navigate('/audit/configs');
      } else {
        const dto: CreateAuditConfigRequest = {
          resource: values.resource,
          actions: values.actions,
          is_enabled: values.is_enabled,
          retention_days: values.retention_days,
          log_details: values.log_details,
          notify_on_action: values.notify_on_action,
        };
        await createAuditConfig(dto);
        message.success(t('audit.config_create_success'));
        navigate('/audit/configs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('audit', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/audit/configs')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('audit.config_edit_title') : t('audit.config_create_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ actions: [], is_enabled: true, retention_days: 90, log_details: true, notify_on_action: false }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="resource"
          label={t('audit.field.resource')}
          rules={[{ required: true, message: t('audit.validation.resource_required') }]}
        >
          <Input disabled={isEdit} placeholder={t('audit.field.resource')} />
        </Form.Item>

        <Form.Item
          name="actions"
          label={t('audit.field.actions')}
          rules={[
            {
              validator: (_, value) =>
                value && value.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('audit.validation.actions_required'))),
            },
          ]}
        >
          <Select mode="multiple" placeholder={t('audit.field.actions')}>
            {ACTIONS.map((a) => (
              <Select.Option key={a} value={a}>
                {t(`audit.action_options.${a}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="is_enabled" label={t('audit.field.is_enabled')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item
          name="retention_days"
          label={t('audit.field.retention_days')}
          rules={[{ type: 'number', min: 1, message: t('audit.validation.retention_days_min') }]}
        >
          <InputNumber min={1} max={3650} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="log_details" label={t('audit.field.log_details')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item name="notify_on_action" label={t('audit.field.notify_on_action')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/audit/configs')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
